import React, { useState, useCallback } from "react";
import api from "../api";
import { fmtCLP, fmtFecha } from "../utils/formato";
import "./InformesCaja.css";

const TABS = [
  { id: "caja", label: "Informe de caja" },
  { id: "ventas", label: "Informe de ventas" },
  { id: "ventas_mes", label: "Ventas por mes" },
  { id: "medios", label: "Informe por medio de pago" },
];

function todayStr() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function firstDayOfMonth() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function fmtMes(value) {
  const datePart = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return value || "";
  const [year, month] = datePart.split("-");
  const nombre = new Intl.DateTimeFormat("es-CL", { month: "long" }).format(
    new Date(Number(year), Number(month) - 1, 1)
  );
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${year}`;
}

export default function InformesCaja({ usuario }) {
  const [tab, setTab] = useState("caja");
  const [desde, setDesde] = useState(firstDayOfMonth());
  const [hasta, setHasta] = useState(todayStr());
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [dataCaja, setDataCaja] = useState(null);
  const [dataVentas, setDataVentas] = useState(null);
  const [dataVentasMes, setDataVentasMes] = useState(null);
  const [dataMedios, setDataMedios] = useState(null);

  const params = useCallback(() => {
    const p = { desde, hasta };
    if (usuario?.rol !== "superadmin" && usuario?.id) p.usuario_id = usuario.id;
    return p;
  }, [desde, hasta, usuario?.id, usuario?.rol]);

  const cargarCaja = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get("/api/caja/informes/caja", { params: params() })
      .then(({ data }) => setDataCaja(data))
      .catch((e) => setError(e?.response?.data?.error || "Error al cargar informe"))
      .finally(() => setCargando(false));
  }, [params]);

  const cargarVentas = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get("/api/caja/informes/ventas", { params: params() })
      .then(({ data }) => setDataVentas(data))
      .catch((e) => setError(e?.response?.data?.error || "Error al cargar informe"))
      .finally(() => setCargando(false));
  }, [params]);

  const cargarVentasMes = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get("/api/caja/informes/ventas-mensuales", { params: params() })
      .then(({ data }) => setDataVentasMes(data))
      .catch((e) => setError(e?.response?.data?.error || "Error al cargar informe"))
      .finally(() => setCargando(false));
  }, [params]);

  const cargarMedios = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get("/api/caja/informes/medios-pago", { params: params() })
      .then(({ data }) => setDataMedios(data))
      .catch((e) => setError(e?.response?.data?.error || "Error al cargar informe"))
      .finally(() => setCargando(false));
  }, [params]);

  const aplicar = () => {
    if (tab === "caja") cargarCaja();
    else if (tab === "ventas") cargarVentas();
    else if (tab === "ventas_mes") cargarVentasMes();
    else cargarMedios();
  };

  return (
    <div className="inf-page">
      <header className="inf-header">
        <h1>Informes</h1>
        <p className="inf-desc">
          Informe de caja, ventas y medios de pago por período.
        </p>
      </header>

      <div className="inf-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`inf-tab ${tab === t.id ? "activo" : ""}`}
            onClick={() => { setTab(t.id); setError(null); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="inf-filters">
        <label>
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label>
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <button type="button" className="inf-btn primary" onClick={aplicar} disabled={cargando}>
          {cargando ? "Cargando…" : "Aplicar"}
        </button>
      </div>

      {error && <div className="inf-alert error">{error}</div>}

      {tab === "caja" && (
        <div className="inf-block">
          <h2>Informe de caja</h2>
          <p className="inf-periodo">Período: {desde} — {hasta}</p>
          {!dataCaja && !cargando && (
            <p className="inf-hint">Elige fechas y pulsa &quot;Aplicar&quot; para cargar el informe.</p>
          )}
          {dataCaja && (
            <div className="inf-caja-grid">
              <section className="inf-card">
                <h3>Aperturas</h3>
                <p className="inf-num">{dataCaja.aperturas?.cantidad ?? 0}</p>
                <p className="inf-monto">{fmtCLP(dataCaja.aperturas?.total)}</p>
              </section>
              <section className="inf-card">
                <h3>Cierres</h3>
                <p className="inf-num">{dataCaja.cierres?.cantidad ?? 0}</p>
                <p className="inf-monto">{fmtCLP(dataCaja.cierres?.total_neto)}</p>
                <div className="inf-desglose">
                  <span>Efectivo: {fmtCLP(dataCaja.cierres?.total_efectivo)}</span>
                  <span>Tarjeta: {fmtCLP(dataCaja.cierres?.total_tarjeta)}</span>
                  <span>Transfer: {fmtCLP(dataCaja.cierres?.total_transfer)}</span>
                  <span>Otro: {fmtCLP(dataCaja.cierres?.total_otro)}</span>
                </div>
              </section>
              <section className="inf-card">
                <h3>Movimientos ingresos</h3>
                <p className="inf-num">{dataCaja.movimientos_ingresos?.cantidad ?? 0}</p>
                <p className="inf-monto">{fmtCLP(dataCaja.movimientos_ingresos?.total)}</p>
              </section>
              <section className="inf-card">
                <h3>Movimientos egresos</h3>
                <p className="inf-num">{dataCaja.movimientos_egresos?.cantidad ?? 0}</p>
                <p className="inf-monto inf-egreso">{fmtCLP(dataCaja.movimientos_egresos?.total)}</p>
              </section>
            </div>
          )}
        </div>
      )}

      {tab === "ventas" && (
        <div className="inf-block">
          <h2>Informe de ventas</h2>
          <p className="inf-periodo">Período: {desde} — {hasta}</p>
          {!dataVentas && !cargando && (
            <p className="inf-hint">Elige fechas y pulsa &quot;Aplicar&quot; para cargar el informe.</p>
          )}
          {dataVentas && (
            <>
              <div className="inf-totales">
                <span>Total pedidos: <strong>{dataVentas.total_pedidos ?? 0}</strong></span>
                <span>Total neto: <strong>{fmtCLP(dataVentas.total_neto)}</strong></span>
              </div>
              <div className="inf-table-wrap">
                <table className="inf-table">
                  <thead>
                    <tr>
                      <th>Día</th>
                      <th className="center">Pedidos</th>
                      <th className="right">Total neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dataVentas.por_dia || []).map((r) => (
                      <tr key={r.dia}>
                        <td>{fmtFecha(r.dia)}</td>
                        <td className="center">{r.pedidos ?? 0}</td>
                        <td className="right">{fmtCLP(r.total_neto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "ventas_mes" && (
        <div className="inf-block">
          <h2>Ventas por mes</h2>
          <p className="inf-periodo">Periodo: {desde} - {hasta}</p>
          {!dataVentasMes && !cargando && (
            <p className="inf-hint">Elige fechas y pulsa &quot;Aplicar&quot; para cargar el informe.</p>
          )}
          {dataVentasMes && (
            <>
              <div className="inf-totales">
                <span>Total pedidos: <strong>{dataVentasMes.total_pedidos ?? 0}</strong></span>
                <span>Total vendido: <strong>{fmtCLP(dataVentasMes.total_neto)}</strong></span>
              </div>
              <div className="inf-table-wrap">
                <table className="inf-table">
                  <thead>
                    <tr>
                      <th>Mes</th>
                      <th className="center">Pedidos</th>
                      <th className="right">Total vendido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dataVentasMes.por_mes || []).map((r) => (
                      <tr key={r.mes}>
                        <td>{fmtMes(r.mes)}</td>
                        <td className="center">{r.pedidos ?? 0}</td>
                        <td className="right">{fmtCLP(r.total_neto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "medios" && (
        <div className="inf-block">
          <h2>Informe por medio de pago</h2>
          <p className="inf-periodo">Período: {desde} — {hasta}</p>
          {!dataMedios && !cargando && (
            <p className="inf-hint">Elige fechas y pulsa &quot;Aplicar&quot; para cargar el informe.</p>
          )}
          {dataMedios && (
            <>
              <div className="inf-totales">
                Total general: <strong>{fmtCLP(dataMedios.total_general)}</strong>
              </div>
              <div className="inf-table-wrap">
                <table className="inf-table">
                  <thead>
                    <tr>
                      <th>Medio de pago</th>
                      <th className="center">Pedidos</th>
                      <th className="right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dataMedios.por_medio || []).map((r) => (
                      <tr key={r.medio_pago || "sin-medio"}>
                        <td>{r.medio_pago || "Sin especificar"}</td>
                        <td className="center">{r.pedidos ?? 0}</td>
                        <td className="right">{fmtCLP(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
